// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Pipeline node registry - nodes that execute in pipelines.
 *
 * Pipeline nodes appear in the pipeline palette and run during pipeline execution.
 * Use registerPipelineNode() for datasources, processing, and analysis nodes.
 */

import type { ComponentType } from 'react'

import type { NodeAdminViewProps, PipelineCategory } from './types'
import { DEFAULT_ORDER, DEFAULT_CATEGORY_ORDER, CATEGORY_PRESETS, startCase } from './registry-utils'

type AnyNodeComponent = ComponentType<any>

export interface PipelineNodeManifest {
  id: string
  name: string
  category: PipelineCategory
  icon: string
  description?: string
  order?: number
}

export interface PipelineNodeRegistration {
  id: string
  manifest: PipelineNodeManifest
  WorkflowNode: AnyNodeComponent
  AdminConfig: ComponentType<NodeAdminViewProps>
}

export interface PipelineNodeDisplay {
  id: string
  label: string
  description: string
  category: PipelineCategory
  categoryLabel: string
  categoryOrder: number
  icon: string
  order: number
}

export interface RegisteredPipelineNode extends PipelineNodeRegistration {
  display: PipelineNodeDisplay
}

const pipelineNodeRegistry = new Map<string, RegisteredPipelineNode>()

function normalizeDisplay(manifest: PipelineNodeManifest): PipelineNodeDisplay {
  const preset = CATEGORY_PRESETS[manifest.category]

  return {
    id: manifest.id,
    label: manifest.name,
    description: manifest.description ?? '',
    category: manifest.category,
    categoryLabel: preset?.label ?? startCase(manifest.category),
    categoryOrder: preset?.order ?? DEFAULT_CATEGORY_ORDER,
    icon: manifest.icon,
    order: manifest.order ?? DEFAULT_ORDER,
  }
}

export function registerPipelineNode(registration: PipelineNodeRegistration): void {
  const { id, manifest } = registration

  if (!manifest.id || !manifest.name || !manifest.category || !manifest.icon) {
    throw new Error(`Pipeline node "${id}": id, name, category, and icon are required`)
  }

  if (pipelineNodeRegistry.has(id)) {
    throw new Error(`Pipeline node "${id}" is already registered`)
  }

  pipelineNodeRegistry.set(id, {
    ...registration,
    display: normalizeDisplay(manifest),
  })
}

export function getPipelineNode(id: string): RegisteredPipelineNode | undefined {
  return pipelineNodeRegistry.get(id)
}

export function getAllPipelineNodes(): RegisteredPipelineNode[] {
  return Array.from(pipelineNodeRegistry.values())
}
